'use client';

import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { loginEmployee } from '@/app/lib/auth';
import { useFlash } from '../../lib/FlashMessageProvider';
import { redirectIfLoggedIn } from '../../lib/sessionCheck';
import { FiEye, FiEyeOff } from "react-icons/fi";

export default function LoginPage() {
  const router = useRouter();
  const { addMessage } = useFlash();
  const [showModal, setShowModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [showNewPasswordModal, setShowNewPasswordModal] = useState(false);
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPasswordText, setShowNewPasswordText] = useState(false);
  const [showConfirmPasswordText, setShowConfirmPasswordText] = useState(false);
  const [showPassword, setShowPassword] = useState(false); // 👈 eye toggle for login

  const [passwordMismatch, setPasswordMismatch] = useState(false);

  // Live validation errors
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Validation functions
  const validateEmail = (email) => {
  if (!email.trim()) return 'Email is required';
  if (/^\s|\s$/.test(email)) {
    return 'Email address cannot have leading or trailing spaces';
  }
  if (/\s/.test(email)) {
    return 'Email address cannot contain spaces';
  }
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return 'Please enter a valid email address';
  }

  return '';
};


  const passwordChecks = [
    { label: 'At least 8 characters', test: (p) => p.length >= 8 },
  ];

  const validatePassword = (password) => {
    if (!password) return 'Password is required';
    if (password.length < 8) return 'Password must be at least 8 characters';
    return '';
  };
  useEffect(() => {
    if (confirmPassword && newPassword !== confirmPassword) {
      setPasswordMismatch(true);
    } else {
      setPasswordMismatch(false);
    }
  }, [newPassword, confirmPassword]);

  // Shared Modal Style
  const modalContainerStyle = {
    background: 'rgba(0,0,0,0.5)',
  };
  const modalDialogStyle = {
    maxWidth: '600px',
    width: '100%',
  };
  const modalContentStyle = {
    width: '100%',
    minHeight: '300px',
    borderRadius: '16px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '20px 25px',
    transition: 'all 0.3s ease-in-out',
  };
  const modalBodyStyle = {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  };

  useEffect(() => {
    redirectIfLoggedIn(router);
  }, [router]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));

    // Live validation
    if (name === 'email') {
      setEmailError(validateEmail(value));
    } else if (name === 'password') {
      setPasswordError(validatePassword(value));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Final validation before submit
    const emailErr = validateEmail(form.email);
    const passwordErr = validatePassword(form.password);
    setEmailError(emailErr);
    setPasswordError(passwordErr);

  if (emailErr || passwordErr) {
  const messages = [];
  if (emailErr) messages.push(emailErr);
  if (passwordErr) messages.push(passwordErr);
  addMessage('warning', messages.join(' & '));
  return;
}

    setLoading(true);
    try {
      const result = await loginEmployee(form);
      console.log('%c🔹 Full login response from backend:', 'color: #00bfa6; font-weight: bold;');
      console.log(JSON.stringify(result, null, 2));

      if (!result.success) {
        addMessage('error', result.message || 'Invalid credentials.');
        return;
      }

      // Store employee session data
      if (result.data) {
        const userRole = result.data.role;
        localStorage.setItem('employee_name', result.data.name);
        localStorage.setItem('organisation', result.data.organisation || '');
        document.cookie = `role=${encodeURIComponent(userRole)}; path=/`;
        document.cookie = `employee_name=${encodeURIComponent(result.data.name)}; path=/`;
        document.cookie = `organisation=${encodeURIComponent(result.data.organisation || '')}; path=/`;
      }

      addMessage('success', 'Login successful!');
      router.push('/employee/home');
    } catch (err) {
      addMessage('error', err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();

    setShowForgotModal(false);
    setShowNewPasswordModal(true);
    localStorage.setItem("reset_email", resetEmail);

    try {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_API_BASE_URL}/employee/resend-otp`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: resetEmail }),
        }
      );
      const data = await res.json();

      if (!res.ok) {
        addMessage("error", data.message || "Failed to send OTP.");
        setShowNewPasswordModal(false);
        setShowForgotModal(true);
        return;
      }
      addMessage("success", data.message);
    } catch (error) {
      console.error(error);
      addMessage("error", "Something went wrong sending OTP.");
      setShowNewPasswordModal(false);
      setShowForgotModal(true);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      addMessage("error", "Passwords do not match.");
      return;
    }

    const savedEmail = localStorage.getItem("reset_email");

    try {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_API_BASE_URL}/employee/reset-password`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: savedEmail,
            otp: otp,
            new_password: newPassword,
          }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        addMessage("error", data.message || "Failed to reset password.");
        return;
      }

      addMessage("success", data.message || "Password reset successfully!");
      localStorage.removeItem("reset_email");
      setShowNewPasswordModal(false);
    } catch (error) {
      console.error(error);
      addMessage("error", "Something went wrong resetting password.");
    }
  };

  return (
    <div className="w-100">
      <h3 className="mb-1">Welcome back</h3>
      <p className="text-muted">
        We're glad to see you again. Ready to continue managing your payroll?
      </p>

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="email">Employee Email</label>
          <input
            type="text"
            name="email"
            className={`custom-input ${emailError ? 'is-invalid' : ''}`}
            placeholder="Employee Email"
            required
            value={form.email}
            onChange={handleChange}
          />
          {emailError && <div className="invalid-feedback d-block">{emailError}</div>}
        </div>

        <div className="form-group mt-3">
          <label htmlFor="password">Password</label>
          <div className="password-wrapper">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              className={`custom-input ${passwordError ? 'is-invalid' : ''}`}
              placeholder="Password"
              required
              value={form.password}
              onChange={handleChange}
            />
            <span className="eye-icon" onClick={() => setShowPassword(!showPassword)}>
              {showPassword ? <FiEyeOff /> : <FiEye />}
            </span>
          </div>
          
          {passwordError && <div className="invalid-feedback d-block">{passwordError}</div>}

          {/* Password checklist — shows only while typing */}
          {form.password.length > 0 && (
            <ul className="list-unstyled mt-2 mb-0" style={{ fontSize: '0.82rem', marginTop: '0.5rem' }}>
              {passwordChecks.map(({ label, test }) => {
                const passed = test(form.password);
                return (
                  <li key={label} style={{ color: passed ? '#198754' : '#dc3545' }}>
                    {passed ? '✅' : '❌'} {label}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="row mt-2 mb-3">
          <div className="col">
            <input type="checkbox" /> Remember my password.
          </div>
          <div className="col text-end">
            <button
              type="button"
              className="btn btn-link text-danger p-0 forgot-text"
              onClick={() => setShowForgotModal(true)}
            >
              Forgot password?
            </button>
          </div>
        </div>

        <button
          type="submit"
          className="btn btn-primary btn-submit w-100"
          disabled={loading}
        >
          {loading ? 'Signing in...' : 'Sign in'}
        </button>

        <div className="divider">
          <hr className="line" />
          <hr className="line" />
        </div>
      </form>

      {/* Forgot Password Modal (unchanged) */}
      {showForgotModal && (
        <div className="modal fade show d-block" style={modalContainerStyle}>
          <div className="modal-dialog modal-dialog-centered" style={modalDialogStyle}>
            <div className="modal-content" style={modalContentStyle}>
              <div className="modal-header pb-0">
                <h5 className="modal-title fw-bold">Forgot Password</h5>
                <button type="button" className="btn-close" onClick={() => setShowForgotModal(false)}></button>
              </div>
              <form onSubmit={handleForgotSubmit} style={modalBodyStyle}>
                <div className="modal-body">
                  <label htmlFor="resetEmail" className="form-label fw-semibold">
                    Enter your email address
                  </label>
                  <input
                    type="email"
                    id="resetEmail"
                    className="form-control"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                  />
                </div>
                <div className="modal-footer d-flex justify-content-end mt-auto">
                  <button
                    type="button"
                    className="py-2 px-4 rounded-pill btn btn-outline-danger"
                    onClick={() => setShowForgotModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="py-2 px-4 btn rounded-pill"
                    style={{ backgroundColor: '#BB3C2D', borderColor: '#BB3C2D', color: 'white' }}
                  >
                    Send Reset OTP
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Set New Password Modal (unchanged) */}
      {showNewPasswordModal && (
        <div className="modal fade show d-block" style={modalContainerStyle}>
          <div className="modal-dialog modal-dialog-centered" style={modalDialogStyle}>
            <div className="modal-content" style={modalContentStyle}>
              <div className="modal-header pb-0">
                <h5 className="modal-title fw-bold">Set New Password</h5>
                <button type="button" className="btn-close" onClick={() => setShowNewPasswordModal(false)}></button>
              </div>
              <form onSubmit={handleResetSubmit}>
                <div className="modal-body pt-3">
                  <label htmlFor="otp" className="form-label fw-semibold">
                    Enter OTP
                  </label>
                  <input
                    type="text"
                    id="otp"
                    className="form-control mb-3"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder="Enter the 6-digit OTP"
                    maxLength="6"
                    required
                  />
                  <div className="position-relative mb-3">
                    <input
                      type={showNewPasswordText ? "text" : "password"}
                      id="newPassword"
                      className="form-control"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                      required
                    />
                    <i
                      className={`bi ${showNewPasswordText ? "bi-eye-slash" : "bi-eye"}`}
                      onClick={() => setShowNewPasswordText((prev) => !prev)}
                      style={{ cursor: "pointer", fontSize: "1.2rem", color: "#999", position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)" }}
                    ></i>
                  </div>
                  <div className="position-relative mb-3">
                    <input
                      type={showConfirmPasswordText ? "text" : "password"}
                      id="confirmPassword"
                      className="form-control"
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        setPasswordMismatch(newPassword !== e.target.value);
                      }}
                      placeholder="Confirm new password"
                      required
                    />
                    <i
                      className={`bi ${showConfirmPasswordText ? "bi-eye-slash" : "bi-eye"}`}
                      onClick={() => setShowConfirmPasswordText((prev) => !prev)}
                      style={{ cursor: "pointer", fontSize: "1.2rem", color: "#999", position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)" }}
                    ></i>
                  </div>
                  {passwordMismatch && <div className="text-danger mb-2">Passwords do not match.</div>}
                </div>
                <div className="modal-footer d-flex justify-content-end">
                  <button type="button" className="py-2 px-4 rounded-pill btn btn-outline-danger" onClick={() => setShowNewPasswordModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="py-2 px-4 btn rounded-pill" style={{ backgroundColor: "#BB3C2D", borderColor: "#BB3C2D", color: "white" }}>
                    Reset Password
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}